/**
 * 인증 API
 * - 회원가입, 로그인, 프로필 조회
 */
import apiClient from './client';

export interface RegisterData {
  email: string;
  password: string;
  name: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  user: {
    id: string;
    email: string;
    name: string;
    subscription: {
      tier: 'free' | 'basic' | 'premium';
      expiresAt?: string;
    };
    profileImage?: string;
    preferences?: { language?: string };
  };
}

export interface User {
  id: string;
  email: string;
  name: string;
  subscription: {
    tier: 'free' | 'basic' | 'premium';
    expiresAt?: string;
  };
  profileImage?: string;
  preferences?: { language?: string };
  createdAt: string;
  lastLoginAt?: string;
  isEmailVerified?: boolean;
}

/**
 * 회원가입
 */
export const register = async (data: RegisterData): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/auth/register', data);
  return response.data;
};

/**
 * 로그인
 */
export const login = async (data: LoginData): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/auth/login', data);
  return response.data;
};

/**
 * 현재 사용자 정보 조회
 */
export const getProfile = async (): Promise<User> => {
  const response = await apiClient.get<User>('/users/me');
  return response.data;
};

/**
 * 프로필 업데이트
 */
export const updateProfile = async (data: { name?: string; profileImage?: string; preferences?: { language?: string } }): Promise<User> => {
  const response = await apiClient.put<User>('/users/me', data);
  return response.data;
};

/**
 * 비밀번호 변경 (인증 필요)
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  const response = await apiClient.post('/auth/password/change', { currentPassword, newPassword });
  return response.data;
};

/**
 * 비밀번호 재설정 요청
 */
export const requestPasswordReset = async (email: string): Promise<{ message: string }> => {
  const response = await apiClient.post('/auth/password/reset', { email });
  return response.data;
};

/**
 * 비밀번호 재설정 (토큰)
 */
export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/auth/password/reset/${token}`, { newPassword });
  return response.data;
};

/**
 * 이메일 검증 요청
 */
export const requestEmailVerification = async (): Promise<{ message: string }> => {
  const response = await apiClient.post('/auth/verify-email/request');
  return response.data;
};

/**
 * 이메일 검증
 */
export const verifyEmail = async (token: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/auth/verify-email/${token}`);
  return response.data;
};

/**
 * 계정 완전 삭제 (GDPR)
 */
export const deleteAccount = async (): Promise<{ message: string }> => {
  const response = await apiClient.delete('/auth/account');
  return response.data;
};

